import angular from 'angular'

import KEY from '../../constants/vd-key-codes'
import AutocompleteInputController from './vd-autocomplete-input.controller'
import suggestionTemplate from './vd-autocomplete-suggestions.html'

/**
 * Controller for the vd-autocomplete component. Also extended by the vd-autocomplete-multi component's controller.
 *
 * @class AutocompleteController
 * @extends {AutocompleteInputController}
 */
export default class AutocompleteController extends AutocompleteInputController {
  constructor(
    $q,
    $scope,
    $element,
    $timeout,
    $window,
    $attrs,
    $log,
    vdSuggestionPopover,
    vdTracking
  ) {
    'ngInject'
    super({ $q, $scope, $element, $timeout, $window, $log })

    this._$attrs = $attrs
    this._vdSuggestionPopover = vdSuggestionPopover
    this._vdTracking = vdTracking
  }

  $onInit() {
    super.$onInit()

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => this._renderModel()
    }
  }

  $postLink() {
    super.$postLink()
    this._$attrs.$addClass('vd-autocomplete-input-container')
  }

  /**
   * Perform a "feeling lucky" search - looking for the best/only match for the current search query and if one is
   * found selecting it.
   *
   * @method feelingLucky
   *
   * @param  {Object} [options]
   *
   *         @param {Suggestion} [options.pendingSuggestion]
   *                The suggestion that would have been selected if feelingLucky was not configured
   */
  feelingLucky(options = {}) {
    this._selectText()
    this._close()

    const response = this.source.feelingLucky({
      query: this.data.query,
      pendingSuggestion: options.pendingSuggestion,
      suggestions: this.data.suggestions,
      selected: this.data.selected,
    })

    if (!response) {
      return
    }

    response
      .then(suggestion => {
        this._trackQueryChosenSuggestion(this.data.query, suggestion)
        return this.select(suggestion)
      })
      .catch(angular.noop)
  }

  // ============================== AutocompleteInputController overrides =============================== //

  /**
   * Select the given suggestion, setting the model and emitting to the onSelect hook.
   *
   * @method select
   * @param  {Suggestion} suggestion
   *
   * @return {Promise} A promise that is resolved if the selection was successful or rejected if the selection
   *         was cancelled/unsuccessful.
   */
  select(suggestion) {
    return super
      .select(suggestion)
      .then(() => {
        this._close()

        if (angular.isFunction(this.onSelect)) {
          this.onSelect({ $event: { selected: this.data.selected } })
        }
      })
      .finally(() => {
        this._$scope.$applyAsync(() => {
          if (this._searchInput[0].matches(':focus')) {
            // Input element already has focus, so calling .focus() on it
            // will not trigger the event handler. Just call the handler instead.
            this._onSearchInputFocus()
          } else {
            this._searchInput[0].focus()
          }
        })
      })
  }

  _selectAsync(promiseForSuggestion) {
    this._close()
    return super._selectAsync(promiseForSuggestion)
  }

  _select(suggestion) {
    super._select(suggestion)

    const displayText = this.source.getDisplayText(suggestion)

    this.data.query = displayText
    // Also set the value of the search input. Occasionally the model hasn't updated by the time we attempt to focus
    // and select the text of the input.
    this._searchInput[0].value = displayText

    this._updateModel(suggestion)

    this._lastSelectedSuggestion = suggestion
  }

  _doCreate() {
    this._close()
    super._doCreate()
  }

  _initSearchInput() {
    super._initSearchInput()
    this._searchInput.on('focus', () => this._onSearchInputFocus())
    this._searchInput.on('blur', () => this._onSearchInputBlur())
  }

  _onQueryChange(query, prevQuery) {
    if (
      this._lastSelectedSuggestion &&
      query === this.source.getDisplayText(this._lastSelectedSuggestion)
    ) {
      return
    }

    if (query !== prevQuery) {
      this._clearModelOnQueryChange()
      this._setTypeaheadHint()

      if (query) {
        this._search(query)
      }
    }

    if (!query) {
      this._close()
    }

    this._lastSelectedSuggestion = null
  }

  _updateSuggestions(data) {
    super._updateSuggestions(data)

    if (this._suggestionListOpen) {
      return
    }

    const popoverOptions = {
      trigger: this._$element,
      template: suggestionTemplate,
      data: this.data,
      source: this.source,
      // Default to lazy load suggestions (uses `vs-repeat` as opposed to `ng-repeat`)
      lazyLoad: this.lazyLoad === undefined ? true : this.lazyLoad,
    }

    if (this.source.create) {
      popoverOptions.create = () => this._doCreate()
    }

    this._suggestionListOpen = true
    this._vdSuggestionPopover.open(popoverOptions).then(suggestion => {
      if (suggestion) {
        this.select(suggestion)
          .then(() => {
            this._trackQueryChosenSuggestion(data.query, suggestion)
          })
          .catch(angular.noop)
      } else if (!this._searchInput[0].matches(':focus')) {
        this._setModelDirty()
      }

      this._suggestionListOpen = false
      this._setTypeaheadHint()
    })
  }

  _handleSearchSourceError(error, query) {
    super._handleSearchSourceError(error, query)

    this._vdSuggestionPopover.openError({
      trigger: this._$element,
      errorTemplate: this.errorTemplate,
    })
  }

  _handleKeyDown(key, event) {
    if (key === KEY.ENTER) {
      if (this._lastSelectedSuggestion) {
        this.select(this._lastSelectedSuggestion).catch(angular.noop)
        return
      }

      // Initiate the create flow here when `allowFeelingLuckyCreate` is false, preventing
      // the creation to occur during the feeling lucky flow. This allows apps to receive
      // the query entered. Web Register makes use of this by adding the entered query as
      // an unknown line item.
      if (
        this._suggestionListOpen &&
        this.source.create &&
        !this.data.suggestions.length &&
        this.data.activeFooter &&
        !this.source.allowFeelingLuckyCreate
      ) {
        this._doCreate()
        return
      }

      if (this.source.feelingLucky && this.data.query) {
        if (!this._suggestionListOpen || !this.data.suggestions.length) {
          this.feelingLucky()
          return
        } else {
          const { suggestions, activeFooter, activeIndex } = this.data
          if (suggestions.length && activeIndex === 0 && !activeFooter) {
            const pendingSuggestion = suggestions[activeIndex]
            this.feelingLucky({ pendingSuggestion })
            return
          }
        }
      }
    }

    super._handleKeyDown(key, event)
  }

  // ============================== Protected Methods =============================== //

  /**
   * Update ng-model (if present) to the given value.
   *
   * @protected
   * @method _updateModel
   * @param  {Suggestion|undefined} modelValue
   */
  _updateModel(modelValue) {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(modelValue)
    }
  }

  /**
   * Update the internal representation of the model to the current $viewValue of ng-model.
   *
   * @protected
   * @method _renderModel
   */
  _renderModel() {
    const suggestion = this.ngModelCtrl.$viewValue
    this.data.selected = suggestion
    this.data.query = suggestion ? this.source.getDisplayText(suggestion) : ''
  }

  /**
   * Clear ng-model's value when the query changes.
   *
   * @protected
   * @method _clearModelOnQueryChange
   */
  _clearModelOnQueryChange() {
    this._updateModel()
  }

  // ============================== Private Methods =============================== //

  _onSearchInputFocus() {
    if (this.ngModelCtrl) {
      this._$scope.$applyAsync(() => this.ngModelCtrl.$setTouched())
    }
    this._selectText()
  }

  _onSearchInputBlur() {
    if (!this._suggestionListOpen) {
      this._setModelDirty()
    }
  }

  _setModelDirty() {
    if (this.ngModelCtrl && this._searchInput.hasClass('ng-dirty')) {
      this._$scope.$applyAsync(() => this.ngModelCtrl.$setDirty())
    }
  }

  _selectText() {
    if (this._searchInput) {
      const searchInput = this._searchInput[0]
      searchInput.setSelectionRange(0, searchInput.value.length)
    }
  }

  _close() {
    this._vdSuggestionPopover.close()
    this._cancelQueue()
  }

  _trackQueryChosenSuggestion(query, selectedSuggestion) {
    if (!query || !selectedSuggestion) {
      return
    }
    this._vdTracking.trackUserEvent({
      eventName: 'Autocomplete Suggestion Selected',
      metadata: {
        Query: query,
        'Selected suggestion': selectedSuggestion.id,
        'Suggestion type': selectedSuggestion.type,
        'Suggestion name': this.source.getDisplayText(selectedSuggestion),
      },
    })
  }
}
